import React, { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { API_BASE_URL } from '../../config';
import './PaymentProcessingModal.css';


const PaymentProcessingModal = ({ payment, onClose, onSuccess }) => {
  const stripe = useStripe();
  const [paymentMethod, setPaymentMethod] = useState('stripe_checkout');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleProcessPayment = async () => {
    if (!payment || !payment.id) {
      setError('Invalid payment information');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      if (paymentMethod === 'stripe_checkout') {
        // Redirect to custom checkout page
        const returnPath = encodeURIComponent(window.location.pathname);
        window.location.href = `/checkout/${payment.id}?return=${returnPath}`;
        return;
      } else if (paymentMethod === 'cash' || paymentMethod === 'check') {
        // Process cash or check payment directly
        const response = await fetch(`${API_BASE_URL}/api/financial/payments/${payment.id}/process`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            payment_method: paymentMethod === 'cash' ? 'Cash' : 'Check'
          })
        });

        const data = await response.json();
        
        if (data.error) {
          setError(data.error);
        } else {
          onSuccess();
        }
        setProcessing(false);
      }
    } catch (err) {
      setError(err.message || 'Failed to process payment');
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="payment-modal-header">
          <h2>Process Payment</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="payment-modal-body">
          <div className="payment-info">
            <div className="info-row">
              <span className="info-label">Tenant:</span>
              <span className="info-value">{payment.tenant_name || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Property:</span>
              <span className="info-value">{payment.property_name || 'N/A'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Amount:</span>
              <span className="info-value amount-highlight">{formatCurrency(payment.amount)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Due Date:</span>
              <span className="info-value">{new Date(payment.due_date).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="payment-method-selection">
            <label className="payment-method-label">Payment Method:</label>
            <div className="payment-method-options">
              <label className="payment-method-option">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="stripe_checkout"
                  checked={paymentMethod === 'stripe_checkout'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>Credit Card / Bank Transfer (Stripe)</span>
              </label>
              <label className="payment-method-option">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={paymentMethod === 'cash'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>Cash</span>
              </label>
              <label className="payment-method-option">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="check"
                  checked={paymentMethod === 'check'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>Check</span>
              </label>
            </div>
          </div>

          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="payment-modal-actions">
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={processing}
            >
              Cancel
            </button>
            <button
              className="process-button"
              onClick={handleProcessPayment}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Process Payment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessingModal;

